import { useCallback, useEffect, useRef, useState } from 'react';
import { salesApi, PlatformApiError, platformTokens, salesPin } from '../lib/platformApi';
// ⚠ THE SAME BASE THE REST OF THE CLIENT USES. platformApi imports it from api.ts precisely so a
// deployment that sets VITE_API_BASE does not end up with one file posting to a different origin —
// and a raw fetch() here is exactly the file that would drift.
import { API_BASE } from '../lib/api';
import {
  recordingQueue, warnBeforeLeaving, pickMimeType, extensionFor, type PendingRecording,
} from '../lib/recordingQueue';

/**
 * ═══ RECORD THE MEETING, THEN LET IT WRITE ITSELF UP ═══
 *
 * ⚠ THE ORDER IS: SAY IT, RECORD IT, KEEP IT, SEND IT LATER. The consent line comes before the
 * button because it is a thing you say out loud in a room, not a checkbox you tick afterwards.
 *
 * ⚠ AND NOTHING THE MODEL PRODUCES GOES INTO THE RECORD UNTIL YOU TAP IT. Each proposal is accepted
 * one at a time. The whole worth of the record is that everything in it was actually said.
 */

interface Proposed { index: number; kind: string; kindLabel: string; body: string; saidBy: string | null; quote: string }
interface Notes {
  summary: string; peopleMet: string[]; points: Proposed[]; numbers: string[];
  nextStep: string | null; nextStepOn: string | null; dropped: string[];
}
export interface Recording {
  id: string; prospectId: string; kind: string; state: string; meetingOn: string;
  ourAttendees: string | null; durationSeconds: number; bytes: number;
  consentAnnounced: boolean; note: string | null; failureReason: string | null;
  transcriberVersion: string | null; costPaise: number;
  createdAt: string; transcribedAt: string | null;
  plan: string | null; notes: Notes | null;
}

const CONSENT_LINE = 'I’ll record this so I don’t miss anything — is that alright?';

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const today = () => new Date().toISOString().slice(0, 10);

export function MeetingRecorder({ prospectId, prospectName, onChanged }: {
  prospectId: string; prospectName: string; onChanged: () => void;
}) {
  const [rows, setRows] = useState<Recording[]>([]);
  const [pending, setPending] = useState<PendingRecording[]>([]);
  const [phase, setPhase] = useState<'idle' | 'recording' | 'saving'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [attendees, setAttendees] = useState('');
  const [announced, setAnnounced] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [kindNow, setKindNow] = useState<'Meeting' | 'VoiceNote'>('Meeting');

  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const stream = useRef<MediaStream | null>(null);
  const ticker = useRef<number | null>(null);

  const reload = useCallback(async () => {
    const [list, queued] = await Promise.all([
      salesApi.get<Recording[]>(`/api/platform/sales/prospects/${prospectId}/recordings`).catch(() => []),
      recordingQueue.all(),
    ]);
    setRows(list);
    setPending(queued.filter((q) => q.prospectId === prospectId));
  }, [prospectId]);

  useEffect(() => { void reload(); }, [reload]);

  // ⚠ Only a generic browser prompt is possible, and it is worth it anyway.
  useEffect(() => warnBeforeLeaving(() => pending.length > 0), [pending.length]);

  const stopTracks = () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (ticker.current) { window.clearInterval(ticker.current); ticker.current = null; }
  };
  useEffect(() => stopTracks, []);

  /**
   * ⚠ THE ONE YOU WILL ACTUALLY USE MOST. Forty seconds in the car park, while half of what you
   * remember is still there. No consent line, because there is nobody else in the car — and no
   * attendee field, because it is you.
   */
  const start = async (kind: 'Meeting' | 'VoiceNote' = 'Meeting') => {
    setError(null);
    setKindNow(kind);
    const mimeType = pickMimeType();
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('The browser would not give access to the microphone.');
      return;
    }
    chunks.current = [];
    const mr = new MediaRecorder(stream.current, mimeType ? { mimeType } : undefined);
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
    recorder.current = mr;
    // ⚠ A timeslice, so a crash halfway through leaves usable chunks rather than one unwritten file.
    mr.start(5000);
    setSeconds(0);
    setPhase('recording');
    ticker.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const stop = async () => {
    const mr = recorder.current;
    if (!mr) return;
    setPhase('saving');

    const finished = new Promise<Blob>((resolve) => {
      mr.onstop = () => resolve(new Blob(chunks.current, { type: mr.mimeType || 'audio/webm' }));
    });
    mr.stop();
    const blob = await finished;
    stopTracks();

    try {
      // The row is created first so the queued audio has an id to belong to.
      const rec = await salesApi.post<Recording>('/api/platform/sales/recordings', {
        prospectId, meetingOn: today(), consentAnnounced: announced,
        ourAttendees: kindNow === 'VoiceNote' ? null : (attendees.trim() || null),
        note: null, kind: kindNow,
      });
      await recordingQueue.put({
        id: rec.id, prospectId, prospectName, seconds,
        mimeType: blob.type, savedAt: Date.now(), blob,
      });
      await reload();
      // ⚠ Tries immediately, and does not care if it fails — it is safely in IndexedDB either way.
      void upload(rec.id);
    } catch (e) {
      setError(e instanceof PlatformApiError
        ? `${e.message} The audio is saved on this device and can be sent later.`
        : 'Could not reach the server. The audio is saved on this device.');
    } finally {
      setPhase('idle');
    }
  };

  /**
   * ⚠ RAW BODY, NOT FETCH-WITH-JSON. The audio goes up as bytes; wrapping thirty megabytes in a
   * base64 JSON field would make it forty and pointlessly slow.
   */
  const upload = async (id: string) => {
    const item = await recordingQueue.get(id);
    if (!item) return;
    setBusy(id); setError(null);
    try {
      const ext = extensionFor(item.mimeType);
      const q = new URLSearchParams({ seconds: String(item.seconds), contentType: item.mimeType });
      const res = await fetch(
        `${API_BASE}/api/platform/sales/recordings/${id}/audio?${q}&ext=${ext}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': item.mimeType || 'audio/webm',
            Authorization: `Bearer ${platformTokens.access() ?? ''}`,
            // ⚠ Read through salesPin, not a hand-typed sessionStorage key. The key changed once
            // already; a second copy of it here is a silent 403 the day it changes again.
            'X-Sales-Pin': salesPin.get(),
          },
          body: item.blob,
        });
      if (!res.ok) throw new Error(await res.text());
      // ⚠ Only now. A queue that clears on send is a queue that loses a meeting to a 502.
      await recordingQueue.remove(id);
      await reload();
      onChanged();
    } catch {
      setError('The upload did not go through. The audio is still on this device — try again.');
    } finally { setBusy(null); }
  };

  const transcribe = async (id: string) => {
    setBusy(id); setError(null);
    try {
      await salesApi.post(`/api/platform/sales/recordings/${id}/transcribe`, {});
      await reload();
    } catch (e) {
      setError(e instanceof PlatformApiError ? e.message : 'Could not transcribe it.');
    } finally { setBusy(null); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginBottom: 14 }}>
      <div style={{ fontWeight: 650, fontSize: 12.5, marginBottom: 6 }}>Meetings</div>

      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 8px' }}>{error}</p>}

      {/* ⚠ THE LINE COMES FIRST, because it is a thing said out loud in a room before the button is
          pressed — not a box ticked afterwards. Without it the recording cannot be transcribed at
          all: the domain refuses rather than warns. */}
      <div style={{
        border: '1px solid var(--line)', borderRadius: 10, padding: 12, marginBottom: 12,
      }}>
        <div style={{ fontSize: 12.5, marginBottom: 8 }}>
          Say this first: <strong>“{CONSENT_LINE}”</strong>
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, marginBottom: 10 }}>
          <input type="checkbox" checked={announced} onChange={(e) => setAnnounced(e.target.checked)} />
          I said it, and they were alright with it
        </label>
        <input className="input" placeholder="Who went, from our side" value={attendees}
               style={{ marginBottom: 10 }} onChange={(e) => setAttendees(e.target.value)} />

        {phase === 'recording' ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 650 }}>
              ● {mmss(seconds)}{kindNow === 'VoiceNote' ? ' · voice note' : ''}
            </span>
            <button className="btn btn-accent" onClick={() => void stop()}>Stop</button>
          </div>
        ) : (
          <button className="btn" disabled={phase === 'saving' || !announced}
                  onClick={() => void start('Meeting')}>
            {phase === 'saving' ? 'Saving…' : 'Start recording'}
          </button>
        )}
        {!announced && phase !== 'recording' && (
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '8px 0 0' }}>
            Recording is only useful if it can be transcribed, and it cannot be until this is ticked.
          </p>
        )}
      </div>

      {/* ⚠ OUTSIDE THE CONSENT BOX, DELIBERATELY. A voice note has no second party, so putting it
          inside a panel headed by a consent line would teach you to tick that box without reading
          it — which is exactly how a consent flag stops meaning anything. */}
      {phase !== 'recording' && (
        <div style={{ marginBottom: 12 }}>
          <button className="btn" disabled={phase === 'saving'} onClick={() => void start('VoiceNote')}>
            + Voice note
          </button>
          <span style={{ fontSize: 11.5, color: 'var(--ink-3)', marginLeft: 8 }}>
            Just you, in the car, before you forget.
          </span>
        </div>
      )}

      {pending.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {pending.map((q) => (
            <div key={q.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12.5, marginBottom: 6 }}>
              <span>On this device · {mmss(q.seconds)} · not sent yet</span>
              <button className="btn" disabled={busy === q.id} onClick={() => void upload(q.id)}>
                {busy === q.id ? 'Sending…' : 'Send now'}
              </button>
            </div>
          ))}
        </div>
      )}

      {rows.map((r) => (
        <RecordingRow key={r.id} r={r} busy={busy === r.id} open={openId === r.id}
                      onOpen={() => setOpenId(openId === r.id ? null : r.id)}
                      onTranscribe={() => void transcribe(r.id)}
                      onChanged={() => { void reload(); onChanged(); }} />
      ))}

      {rows.length === 0 && pending.length === 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0 }}>No meetings recorded yet.</p>
      )}
    </div>
  );
}

function RecordingRow({ r, busy, open, onOpen, onTranscribe, onChanged }: {
  r: Recording; busy: boolean; open: boolean;
  onOpen: () => void; onTranscribe: () => void; onChanged: () => void;
}) {
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [alsoLog, setAlsoLog] = useState(true);
  const [saving, setSaving] = useState(false);

  const accept = async () => {
    setSaving(true);
    try {
      await salesApi.post(`/api/platform/sales/recordings/${r.id}/accept`, {
        indexes: [...chosen],
        alsoLogTheSummary: alsoLog,
        nextFollowUpOn: r.notes?.nextStepOn ?? null,
      });
      setChosen(new Set());
      onChanged();
    } catch { /* the list reloads either way */ }
    finally { setSaving(false); }
  };

  const toggle = (i: number) => setChosen((s) => {
    const next = new Set(s);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  return (
    <div style={{ borderTop: '1px solid var(--line-2)', padding: '8px 0' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <button className="btn" style={{ padding: '2px 8px', fontSize: 11 }} onClick={onOpen}>
          {open ? '−' : '+'}
        </button>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>
          {r.meetingOn}{r.kind === 'VoiceNote' ? ' · voice note' : ''}
        </span>
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          {mmss(r.durationSeconds)}
          {r.ourAttendees ? ` · ${r.ourAttendees}` : ''}
          {r.consentAnnounced ? '' : ' · not announced'}
          {` · ${r.state}`}
        </span>
        {(r.state === 'Uploaded' || r.state === 'Failed') && (
          <button className="btn" disabled={busy} onClick={onTranscribe} style={{ marginLeft: 'auto' }}>
            {busy ? 'Working…' : 'Transcribe'}
          </button>
        )}
      </div>

      {r.failureReason && (
        <p style={{ fontSize: 12, color: 'var(--danger)', margin: '4px 0 0' }}>{r.failureReason}</p>
      )}
      {r.plan && r.state === 'Uploaded' && (
        <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '4px 0 0' }}>{r.plan}</p>
      )}

      {open && r.notes && (
        <div style={{ marginTop: 10 }}>
          {r.notes.summary && (
            <p style={{ fontSize: 12.5, color: 'var(--ink-2)', margin: '0 0 8px', lineHeight: 1.55 }}>
              {r.notes.summary}
            </p>
          )}

          {r.notes.points.length > 0 && (
            <>
              <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>
                Proposed — tick what actually happened
              </div>
              {r.notes.points.map((p) => (
                <label key={p.index} style={{
                  display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 7, cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={chosen.has(p.index)} onChange={() => toggle(p.index)}
                         style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 12.5 }}>
                    <span style={{ color: 'var(--ink-3)' }}>{p.kindLabel}: </span>
                    {p.body}
                    {p.saidBy ? <span style={{ color: 'var(--ink-3)' }}> — {p.saidBy}</span> : null}
                    {/* ⚠ THE QUOTE IS SHOWN, ALWAYS. It is the evidence the item is real, and it is
                        the only thing that lets you tell a good extraction from a plausible one
                        without opening the transcript. */}
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                      “{p.quote}”
                    </span>
                  </span>
                </label>
              ))}
            </>
          )}

          {r.notes.dropped.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {/* ⚠ SHOWN, NOT SWALLOWED. Silent dropping is how you end up trusting an extractor
                  that is quietly discarding half of every meeting. */}
              <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>Left out</div>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {r.notes.dropped.map((d, i) => (
                  <li key={i} style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 2 }}>{d}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
              <input type="checkbox" checked={alsoLog} onChange={(e) => setAlsoLog(e.target.checked)} />
              Also log the summary as a visit
            </label>
            <button className="btn btn-accent" disabled={saving || (chosen.size === 0 && !alsoLog)}
                    onClick={() => void accept()}>
              {saving ? 'Saving…' : `Accept ${chosen.size || ''}`.trim()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
